import { createColumnHelper, VisibilityState } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { LuPlus } from 'react-icons/lu';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CommandCenterHousehold, HouseholdType } from '@ndvr/customer-api';

import { useTableColumnSettings } from '@/cip/widgets/Table/useTableColumnSettings';
import { TableSettings } from '@/components/table/TableSettings';
import { Button } from '@/components/ui/button';
import { Page, PageContent, PageTitle, PageTopBar } from '@/components/ui/page-layout';
import { InternalUserNameResolver, useInternalUserDisplayName } from '@/modules/internal-user-queries';
import { AdvisorLoginButton, PersonDrawer } from 'modules';
import {
    formatCurrency,
    getHouseholdName,
    getPrimaryManagerName,
    useHouseholdRoutePaths,
    useHouseholdRouteType,
} from 'utils';
import { useAuthContext } from 'views/auth/Auth';
import { ClientFiltersContextProvider } from 'views/client/ClientFilters/ClientFiltersContext';
import { ClientsListContent } from 'views/client/ClientsListPage';
import { PersonFiltersContextProvider } from 'views/person/PersonFilters/PersonFiltersContext';
import { PersonListContent } from 'views/person/PersonListPage';
import {
    CustomTablePage,
    DateTimeDisplay,
    HorizontalLayout,
    HouseholdLogoCard,
    IconLogoutSquare,
    PersonLogoCard,
    TextLink,
    VerticalLayout,
} from 'widgets';

import { useCommandCenterHouseholds } from './household-queries';
import { HouseholdDetailsDrawer } from './HouseholdDetailsDrawer';
import { HouseholdDrawer } from './HouseholdDrawer';
import { HouseholdFiltersContextProvider } from './HouseholdFilters/HouseholdFiltersContext';
import { HouseholdFilterForm } from './HouseholdFilters/HouseholdFiltersForm';
import { useHouseholdFilters } from './HouseholdFilters/HouseholdFiltersHook';
import { ViewSelector, ViewType } from './ViewSelector';

const columnHelper = createColumnHelper<CommandCenterHousehold>();

const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
    latestLogin: false,
    primaryAdvisor: false,
};

const HOUSEHOLD_LIST_EXPORT_PORTAL_ID = 'household-list-export';
const PERSON_LIST_EXPORT_PORTAL_ID = 'person-list-export';
const CLIENT_LIST_EXPORT_PORTAL_ID = 'client-list-export';

export const HouseholdListPage = () => {
    const [searchParams] = useSearchParams();
    const currentView = (searchParams.get('view') as ViewType) || 'all';

    // Render different views with unified header based on the query parameter
    if (currentView === 'persons') {
        return (
            <PersonFiltersContextProvider>
                <PersonsView />
            </PersonFiltersContextProvider>
        );
    }

    if (currentView === 'account-owners') {
        return (
            <ClientFiltersContextProvider>
                <ClientsView />
            </ClientFiltersContextProvider>
        );
    }

    // Default view: households
    return (
        <HouseholdFiltersContextProvider>
            <HouseholdList />
        </HouseholdFiltersContextProvider>
    );
};

const HouseholdList = () => {
    const navigate = useNavigate();
    const { isProspectRoute } = useHouseholdRouteType();
    const { HouseholdIndividualPageRoutePath } = useHouseholdRoutePaths();
    const [newHouseholdDrawerOpen, setNewHouseholdDrawerOpen] = useState(false);
    const [selectedHousehold, setSelectedHousehold] = useState<CommandCenterHousehold | null>(null);
    const [filteredRowCount, setFilteredRowCount] = useState(0);
    const { data: commandCenterHouseholds = [], isLoading } = useCommandCenterHouseholds();
    const { data: filteredHouseholds } = useHouseholdFilters(commandCenterHouseholds);
    const households = useMemo(
        () =>
            isProspectRoute
                ? commandCenterHouseholds.filter((h) => h.householdType === HouseholdType.PROSPECT) // info: no filters on prospect page
                : filteredHouseholds.filter(
                      (h) => h.householdType !== HouseholdType.PROSPECT && h.householdType !== HouseholdType.ONBOARDING,
                  ),
        [isProspectRoute, commandCenterHouseholds, filteredHouseholds],
    );
    const {
        columnVisibility,
        setColumnVisibility,
        columnSizing,
        setColumnSizing,
        columnOrder,
        setColumnOrder,
        resetSettings,
    } = useTableColumnSettings('households', {
        defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    });
    const getInternalUserName = useInternalUserDisplayName();
    const columns = useMemo(
        () => createColumns(isProspectRoute, resetSettings, getInternalUserName),
        [isProspectRoute, resetSettings, getInternalUserName],
    );

    return (
        <Page>
            <PageTopBar />
            {newHouseholdDrawerOpen && (
                <HouseholdDrawer
                    onClose={() => setNewHouseholdDrawerOpen(false)}
                    onSuccess={(globalAccountGUID) => navigate(HouseholdIndividualPageRoutePath(globalAccountGUID))}
                    isProspectRoute={isProspectRoute}
                />
            )}

            {selectedHousehold ? (
                <HouseholdDetailsDrawer household={selectedHousehold} onClose={() => setSelectedHousehold(null)} />
            ) : null}

            <HouseholdFilterForm
                centerContent={isProspectRoute ? <PageTitle>Prospects</PageTitle> : <ViewSelector />}
                count={isLoading ? undefined : filteredRowCount}
                countLabel={isProspectRoute ? 'prospects' : 'households'}
                rightActions={
                    <>
                        <Button
                            variant="outline2"
                            size="sm"
                            className="px-3"
                            onClick={() => setNewHouseholdDrawerOpen(true)}
                            data-testid="new-household-button"
                            aria-label={isProspectRoute ? 'New Prospect' : 'New Household'}
                        >
                            {isProspectRoute ? (
                                <>
                                    <LuPlus className="h-4 w-4" /> Prospect
                                </>
                            ) : (
                                <>
                                    <LuPlus className="h-4 w-4" /> Household
                                </>
                            )}
                        </Button>
                        <div id={HOUSEHOLD_LIST_EXPORT_PORTAL_ID} className="flex items-center" />
                    </>
                }
            />

            <PageContent scroll="children" padding="none">
                <CustomTablePage
                    rowOnClick={(account) => setSelectedHousehold(account)}
                    columns={columns}
                    data={households}
                    defaultSortCol="latestLogin"
                    defaultSortDirection="desc"
                    isLoading={isLoading}
                    exportName="households"
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                    columnSizing={columnSizing}
                    onColumnSizingChange={setColumnSizing}
                    columnOrder={columnOrder}
                    onColumnOrderChange={setColumnOrder}
                    onFilteredRowCountChange={setFilteredRowCount}
                    cardClassName="m-0 border-0 shadow-none"
                    exportPortalId={HOUSEHOLD_LIST_EXPORT_PORTAL_ID}
                />
            </PageContent>
        </Page>
    );
};

const createColumns = (
    isProspectRoute: boolean,
    resetSettings: () => void,
    getInternalUserName: InternalUserNameResolver,
) => [
    columnHelper.accessor((data) => getHouseholdName(data), {
        id: 'name',
        header: isProspectRoute ? 'Prospect Name' : 'Household Name',
        cell: ({ getValue, row }) => (
            <HouseholdCell value={getValue()} globalAccountGUID={row.original.globalAccountGUID} />
        ),
        sortingFn: 'alphanumeric',
        size: 220,
        minSize: 120,
    }),
    columnHelper.accessor((data) => getPrimaryManagerName(data), {
        id: 'principal',
        header: 'Principal',
        cell: ({ row }) => (
            <PersonLogoCard
                personInfo={{
                    firstName: row.original.primaryManagerFirstNamePlain,
                    lastName: row.original.primaryManagerLastNamePlain,
                    primaryEmail: row.original.primaryManagerEmailAddressPlain,
                    personOrgGUID: row.original.primaryManagerPersonOrgGUID,
                }}
            />
        ),
        sortingFn: 'alphanumeric',
        size: 180,
        minSize: 80,
    }),
    columnHelper.accessor((data) => data?.aum, {
        id: 'aum',
        header: 'AUM',
        cell: ({ getValue, row }) => (
            <TextLink onClick={(ev) => ev.stopPropagation()} to={`/household/${row.original.globalAccountGUID}`}>
                {!Number.isNaN(Number(getValue())) ? formatCurrency(getValue(), 'USD') : null}
            </TextLink>
        ),
        sortingFn: 'alphanumeric',
        sortUndefined: -1,
        size: 135,
        minSize: 80,
    }),
    columnHelper.accessor('latestLoginOnAccount', {
        id: 'latestLogin',
        header: 'Latest Login',
        cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
        sortingFn: 'datetime',
        sortUndefined: -1,
        size: 135,
        minSize: 80,
    }),
    columnHelper.accessor('startDate', {
        id: 'startDate',
        header: 'Client Signed',
        cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
        sortingFn: 'datetime',
        sortUndefined: -1,
        size: 120,
        minSize: 120,
    }),
    columnHelper.accessor((data) => getInternalUserName(data.primaryAdvisorGUID), {
        id: 'primaryAdvisor',
        header: 'Primary Advisor',
        sortingFn: 'alphanumeric',
        enableGlobalFilter: false,
        size: 180,
        minSize: 80,
    }),
    columnHelper.display({
        id: 'actions',
        header: ({ table }) => (
            <div className="flex items-center justify-center" onClick={(ev) => ev.stopPropagation()}>
                <TableSettings table={table} onReset={resetSettings} iconOnly />
            </div>
        ),
        cell: ({ row }) => (
            <HorizontalLayout onClick={(ev) => ev.stopPropagation()} $gap={5}>
                <AdvisorLoginButton
                    size="icon"
                    redirect="/overview"
                    globalAccountGUID={row.original.globalAccountGUID}
                />
            </HorizontalLayout>
        ),
        size: 48,
        minSize: 48,
        meta: { isDisplayCol: true },
    }),
];

const HouseholdCell = (props: { globalAccountGUID: string; value: string }) => {
    const { HouseholdIndividualPageRoutePath } = useHouseholdRoutePaths();
    return <HouseholdLogoCard name={props.value} to={HouseholdIndividualPageRoutePath(props.globalAccountGUID)} />;
};

const PersonsView = () => {
    const [showPersonCreateDrawer, setShowPersonCreateDrawer] = useState(false);
    const { currentTenant } = useAuthContext();

    return (
        <Page>
            <PageTopBar />
            <PersonListContent
                centerContent={<ViewSelector />}
                exportPortalId={PERSON_LIST_EXPORT_PORTAL_ID}
                rightActions={
                    <>
                        <Button variant="outline2" size="sm" onClick={() => setShowPersonCreateDrawer(true)}>
                            <LuPlus className="h-4 w-4" /> Person
                        </Button>
                        <div id={PERSON_LIST_EXPORT_PORTAL_ID} className="flex items-center" />
                    </>
                }
            />
            {showPersonCreateDrawer && (
                <PersonDrawer
                    onClose={() => setShowPersonCreateDrawer(false)}
                    mode="create-tenant"
                    tenantGUID={currentTenant.tenantGUID}
                />
            )}
        </Page>
    );
};

const ClientsView = () => {
    return (
        <Page>
            <PageTopBar />
            <ClientsListContent
                centerContent={<ViewSelector />}
                exportPortalId={CLIENT_LIST_EXPORT_PORTAL_ID}
                rightActions={<div id={CLIENT_LIST_EXPORT_PORTAL_ID} className="contents" />}
            />
        </Page>
    );
};
